import { Link } from 'react-router-dom'
import { ClipboardCheck, ChevronRight } from 'lucide-react'

interface Props {
  /** 카드 변형 */
  variant?: 'home' | 'facility-detail' | 'compact'
  /** 시설 상세에서 호출 시 — 표시용 시설명 */
  facilityName?: string
  /** 시설 상세에서 호출 시 — 결과 페이지에서 단일 시설 자동 검증 카드를 노출하기 위한 컨텍스트 */
  facilityId?: number
}

/**
 * 시설 선택 체크리스트 진입 CTA 카드.
 *
 * <p>plan §5.1 진입 경로 — 메인 홈, 시설 상세 페이지, 검색 결과 배너 등에서 재사용.</p>
 */
export default function ChecklistPromoCard({ variant = 'home', facilityName, facilityId }: Props) {
  // 시설 맥락은 쿼리 대신 router state 로 넘긴다 — 렌더된 href 는 항상 /checklist.
  // 시설마다 /checklist?facilityId={id} 가 생기면 크롤러가 수만 개 URL 을 긁는다 (ssr-spec §17.2.1).
  // 진입 페이지는 resolveEntryFacilityId(lib/checklist/entryFacility.ts)로 state 를 읽는다.
  const checklistState = facilityId != null ? { facilityId } : undefined

  if (variant === 'compact') {
    return (
      <Link
        to="/checklist"
        state={checklistState}
        className="flex items-center gap-3 rounded-lg bg-gradient-to-r from-cb-primary/10 to-cb-primary/5 px-4 py-3 transition hover:from-cb-primary/20 hover:to-cb-primary/10"
      >
        <ClipboardCheck className="h-5 w-5 flex-shrink-0 text-cb-primary" />
        <div className="flex-1 text-sm">
          <span className="font-semibold text-gray-900">60문항 체크리스트</span>
          <span className="ml-1 text-gray-600">로 시설 비교하기</span>
        </div>
        <ChevronRight className="h-4 w-4 flex-shrink-0 text-cb-primary" />
      </Link>
    )
  }

  if (variant === 'facility-detail') {
    return (
      <Link
        to="/checklist"
        state={checklistState}
        className="block rounded-xl border border-cb-primary/20 bg-cb-primary/5 p-4 transition hover:border-cb-primary/40 hover:bg-cb-primary/10"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-cb-primary text-white">
            <ClipboardCheck className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-gray-900">
              내 기준에 얼마나 맞는지 확인하기
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-gray-600">
              60가지 항목에 응답하면 {facilityName ? `${facilityName} ` : '이 시설 '}
              자동 검증 결과를 한눈에 보여드려요.
            </p>
            <span className="mt-2 inline-flex items-center gap-0.5 text-xs font-semibold text-cb-primary">
              체크리스트 시작 <ChevronRight size={14} />
            </span>
          </div>
        </div>
      </Link>
    )
  }

  // variant === 'home'
  return (
    <Link
      to="/checklist"
      className="flex items-center gap-4 rounded-2xl border border-cb-gray-100 bg-gradient-to-br from-cb-primary to-cb-primary-hover p-5 text-white transition hover:brightness-110"
    >
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white/20">
        <ClipboardCheck className="h-6 w-6" />
      </div>
      <div className="flex-1">
        <div className="text-[11px] font-semibold opacity-90">시설 선택 체크리스트</div>
        <h3 className="mt-0.5 text-base font-bold">꼭 확인할 60가지로 시설 비교하기</h3>
        <p className="mt-1 text-xs opacity-80">
          법·인력·비용·의료 자동 검증 · 약 5분
        </p>
      </div>
      <ChevronRight className="h-5 w-5 flex-shrink-0 opacity-80" />
    </Link>
  )
}
